// RamsRequestDetail — view/act-on a single RAMS request: recipients, message,
// secure link, reassignment (consultant/due date), and lifecycle actions.
import { useEffect, useState } from "react";
import {
    X, Loader2, Send, Copy, Archive as ArchiveIcon, Link2, Save, ClipboardList, ChevronDown, ChevronUp,
    Sparkles, MessageCircleQuestion, CheckCircle2, Undo2, FileDown, FileText, History, UploadCloud,
    ThumbsUp, Activity,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, pollGenerateStatus, filenameFromResponse } from "@/lib/api";
import Pill from "@/components/Pill";
import RamsRuntimeForm from "./RamsRuntimeForm";
import RamsDraftPanel from "./RamsDraftPanel";
import RamsSupportingContextPicker from "./RamsSupportingContextPicker";

const inputCls = "w-full h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-sm";
// Mirrors backend RAMS_TOUCHABLE_STATUSES — statuses where the questionnaire
// (and therefore AI pre-fill) is still open for editing.
const RAMS_PREFILL_STATUSES = ["Questionnaire Issued", "Opened", "In Progress", "Clarification Required"];

export default function RamsRequestDetail({ requestId, onClose, onChanged, canEdit }) {
    const [req, setReq] = useState(null);
    const [staff, setStaff] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingReassign, setSavingReassign] = useState(false);
    const [consultantId, setConsultantId] = useState("");
    const [dueDate, setDueDate] = useState("");
    const [runtimeOpen, setRuntimeOpen] = useState(false);
    const [runtime, setRuntime] = useState(null);
    const [runtimeVersion, setRuntimeVersion] = useState(0);
    const [runtimeLoading, setRuntimeLoading] = useState(false);
    const [clarifyOpen, setClarifyOpen] = useState(false);
    const [clarifyNote, setClarifyNote] = useState("");
    const [clarifyBusy, setClarifyBusy] = useState(false);
    const [generateBusy, setGenerateBusy] = useState(false);
    const [reviewBusy, setReviewBusy] = useState(false);
    const [changesOpen, setChangesOpen] = useState(false);
    const [changesNote, setChangesNote] = useState("");
    const [changesBusy, setChangesBusy] = useState(false);
    const [approveBusy, setApproveBusy] = useState(false);
    const [supersedeOpen, setSupersedeOpen] = useState(false);
    const [supersedeNote, setSupersedeNote] = useState("");
    const [supersedeBusy, setSupersedeBusy] = useState(false);
    const [exportBusy, setExportBusy] = useState("");
    const [publishBusy, setPublishBusy] = useState(false);
    const [ackOpen, setAckOpen] = useState(false);
    const [ackNote, setAckNote] = useState("");
    const [ackBusy, setAckBusy] = useState(false);
    const [auditOpen, setAuditOpen] = useState(false);
    const [auditEntries, setAuditEntries] = useState(null);
    const [auditLoading, setAuditLoading] = useState(false);
    const [supportingDocIds, setSupportingDocIds] = useState([]);
    const [prefillDocIds, setPrefillDocIds] = useState([]);
    const [prefillBusy, setPrefillBusy] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/rams/requests/${requestId}`);
            setReq(data);
            setConsultantId(data.assigned_consultant_id || "");
            setDueDate(data.due_date || "");
        } catch (e) { toast.error("Could not load request"); onClose(); }
        setLoading(false);
    };
    // Silent variant — refreshes `req` in the background WITHOUT flipping the
    // top-level `loading` flag, so it never unmounts the embedded questionnaire
    // panel (used right after a submit, so RamsRuntimeForm's own 'done' screen
    // stays visible instead of being torn down and remounted mid-transition).
    const silentRefresh = async () => {
        try {
            const { data } = await api.get(`/rams/requests/${requestId}`);
            setReq(data);
        } catch { /* ignore — non-critical background refresh */ }
    };
    useEffect(() => { load(); api.get("/forms/staff").then((r) => setStaff(r.data)).catch(() => { }); }, [requestId]);

    const act = async (fn, successMsg) => {
        try {
            await fn();
            toast.success(successMsg);
            await load();
            onChanged?.();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Action failed"); }
    };

    const issue = () => act(() => api.post(`/rams/requests/${requestId}/issue`), "Issued");
    const duplicate = () => act(async () => {
        const { data } = await api.post(`/rams/requests/${requestId}/duplicate`);
        toast.success(`Duplicated as ${data.reference}`);
        onChanged?.();
        onClose();
    }, "");
    const archive = () => act(() => api.post(`/rams/requests/${requestId}/archive`), "Archived");

    const saveReassign = async () => {
        setSavingReassign(true);
        try {
            await api.patch(`/rams/requests/${requestId}/reassign`, {
                assigned_consultant_id: consultantId || null,
                assigned_consultant_name: staff.find((s) => s.id === consultantId)?.name,
                due_date: dueDate || null,
            });
            toast.success("Updated");
            await load();
            onChanged?.();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not update"); }
        setSavingReassign(false);
    };

    const copyLink = () => {
        const url = `${window.location.origin}/rams/s/${req.secure_token}`;
        navigator.clipboard.writeText(url);
        toast.success("Secure link copied");
    };

    const toggleRuntime = async () => {
        if (runtimeOpen) { setRuntimeOpen(false); return; }
        setRuntimeOpen(true);
        if (runtime) return;
        setRuntimeLoading(true);
        try {
            const { data } = await api.get(`/rams/requests/${requestId}/runtime`);
            setRuntime(data);
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not load questionnaire"); setRuntimeOpen(false); }
        setRuntimeLoading(false);
    };

    const prefillAnswers = async () => {
        setPrefillBusy(true);
        try {
            await api.post(`/rams/requests/${requestId}/ai-prefill`, { supporting_document_ids: prefillDocIds });
            await pollGenerateStatus(`/rams/requests/${requestId}/ai-prefill/status`);
            const { data } = await api.get(`/rams/requests/${requestId}/runtime`);
            setRuntime(data);
            setRuntimeVersion((v) => v + 1);
            setRuntimeOpen(true);
            toast.success("Pre-filled from the CPP/documents — please review every answer before submitting");
        } catch (e) { toast.error(e.response ? (formatApiError(e.response?.data?.detail) || "AI pre-fill failed") : (e.message || "AI pre-fill failed")); }
        setPrefillBusy(false);
    };

    const requestClarification = async () => {
        if (!clarifyNote.trim()) { toast.error("Add a note describing what's missing or unclear"); return; }
        setClarifyBusy(true);
        try {
            await api.post(`/rams/requests/${requestId}/request-clarification`, { note: clarifyNote.trim() });
            toast.success("Clarification requested — the client has been notified");
            setClarifyOpen(false);
            setClarifyNote("");
            await load();
            onChanged?.();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not request clarification"); }
        setClarifyBusy(false);
    };

    const generateDraft = async () => {
        setGenerateBusy(true);
        try {
            await api.post(`/rams/requests/${requestId}/generate-draft`, { supporting_document_ids: supportingDocIds });
            await pollGenerateStatus(`/rams/requests/${requestId}/generate-draft/status`);
            toast.success("AI draft generated");
            await load();
            onChanged?.();
        } catch (e) { toast.error(e.response ? (formatApiError(e.response?.data?.detail) || "AI generation failed") : (e.message || "AI generation failed")); }
        setGenerateBusy(false);
    };

    const sendForReview = async () => {
        setReviewBusy(true);
        try {
            await api.post(`/rams/requests/${requestId}/send-for-review`, {});
            toast.success("Sent for Priority Risk review");
            await load();
            onChanged?.();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not send for review"); }
        setReviewBusy(false);
    };

    const requestChanges = async () => {
        if (!changesNote.trim()) { toast.error("Add a note describing what needs changing"); return; }
        setChangesBusy(true);
        try {
            await api.post(`/rams/requests/${requestId}/request-changes`, { note: changesNote.trim() });
            toast.success("Sent back for changes");
            setChangesOpen(false);
            setChangesNote("");
            await load();
            onChanged?.();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not request changes"); }
        setChangesBusy(false);
    };

    const approve = async () => {
        setApproveBusy(true);
        try {
            await api.post(`/rams/requests/${requestId}/approve`, {});
            toast.success("Approved — branded output saved to the Document Library");
            await load();
            onChanged?.();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Approval failed"); }
        setApproveBusy(false);
    };

    const supersede = async () => {
        setSupersedeBusy(true);
        try {
            await api.post(`/rams/requests/${requestId}/supersede`, { note: supersedeNote || null });
            toast.success("Request superseded");
            setSupersedeOpen(false);
            setSupersedeNote("");
            await load();
            onChanged?.();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not supersede"); }
        setSupersedeBusy(false);
    };

    const exportFile = async (kind) => {
        setExportBusy(kind);
        try {
            const res = await api.get(`/rams/requests/${requestId}/export/${kind}`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = filenameFromResponse(res, `${req.reference}.${kind}`);
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error(`${kind.toUpperCase()} export failed`); }
        setExportBusy("");
    };

    const publish = async () => {
        setPublishBusy(true);
        try {
            await api.post(`/rams/requests/${requestId}/publish`, {});
            toast.success("Published to client — visible in their Document Library");
            await load();
            onChanged?.();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not publish"); }
        setPublishBusy(false);
    };

    const acknowledge = async () => {
        setAckBusy(true);
        try {
            await api.post(`/rams/requests/${requestId}/acknowledge`, { note: ackNote || null });
            toast.success("Acknowledged — thank you");
            setAckOpen(false);
            setAckNote("");
            await load();
            onChanged?.();
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not acknowledge"); }
        setAckBusy(false);
    };

    const toggleAudit = async () => {
        if (auditOpen) { setAuditOpen(false); return; }
        setAuditOpen(true);
        if (auditEntries) return;
        setAuditLoading(true);
        try {
            const { data } = await api.get(`/rams/requests/${requestId}/audit-trail`);
            setAuditEntries(data.entries || []);
        } catch (e) { toast.error("Could not load activity"); setAuditOpen(false); }
        setAuditLoading(false);
    };

    const downloadPublished = async () => {
        setExportBusy("published");
        try {
            const res = await api.get(`/documents/${req.output_document_id}/download`, { responseType: "blob" });
            const url = URL.createObjectURL(res.data);
            const a = document.createElement("a");
            a.href = url;
            a.download = filenameFromResponse(res, `${req.reference}.pdf`);
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error("Download failed"); }
        setExportBusy("");
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" data-testid="rams-request-detail">
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[88vh] flex flex-col overflow-hidden">
                <div className="px-5 py-4 border-b border-[#E5E0DA] flex items-center justify-between">
                    <h3 className="text-base font-bold text-[#1F1F1F]">{loading ? "Loading…" : req?.reference}</h3>
                    <button onClick={onClose} className="p-1 text-[#9A968F] hover:text-[#1F1F1F]"><X size={16} /></button>
                </div>
                {loading || !req ? (
                    <div className="p-10 text-center text-[#9A968F]"><Loader2 className="inline animate-spin" size={16} /></div>
                ) : (
                    <div className="p-5 space-y-4 overflow-y-auto flex-1">
                        <div className="flex items-center gap-2">
                            <Pill tone="accent">{req.status}</Pill>
                            {req.percentage_completed > 0 && <span className="text-[11px] text-[#9A968F]">{req.percentage_completed}% complete</span>}
                        </div>
                        <div>
                            <p className="text-sm font-bold text-[#1F1F1F]">{req.title}</p>
                            <p className="text-[12px] text-[#6F6A64]">{req.template_name}</p>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-[12px]">
                            <InfoRow label="Recipients" value={req.recipients.map((r) => r.name).join(", ") || "—"} />
                            <InfoRow label="Message" value={req.message || "—"} />
                        </div>
                        {req.secure_link_enabled && req.secure_token && (
                            <button data-testid="rams-detail-copy-link" onClick={copyLink}
                                className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-[#2A5DB0] hover:underline"><Link2 size={12} />Copy secure link</button>
                        )}

                        {req.status !== "Draft Request" && (
                            <div className="pt-3 border-t border-[#F0EDE8]">
                                {req.self_generated && RAMS_PREFILL_STATUSES.includes(req.status) && (
                                    <div className="mb-3 space-y-2">
                                        <RamsSupportingContextPicker requestId={requestId} selectedIds={prefillDocIds} onChange={setPrefillDocIds} />
                                        <button data-testid="rams-prefill-btn" onClick={prefillAnswers} disabled={prefillBusy}
                                            className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-1.5 rounded-lg border bg-white text-[#1F1F1F] hover:bg-[#F6F4F0] disabled:opacity-60"
                                            style={{ borderColor: "#E5E0DA" }}>
                                            {prefillBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />} AI pre-fill from documents
                                        </button>
                                    </div>
                                )}
                                <button data-testid="rams-detail-toggle-questionnaire" onClick={toggleRuntime}
                                    className="w-full flex items-center justify-between text-[12.5px] font-bold text-[#1F1F1F]">
                                    <span className="flex items-center gap-1.5"><ClipboardList size={13} /> Questionnaire responses</span>
                                    {runtimeOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                </button>
                                {runtimeOpen && (
                                    runtimeLoading ? <div className="py-6 text-center text-[#9A968F]"><Loader2 className="inline animate-spin" size={14} /></div> :
                                        runtime && (
                                            <div className="mt-3 p-3.5 rounded-xl bg-[#FBFAF8] border border-[#F0EDE8]">
                                                {req.self_generated && Object.keys(runtime.answers || {}).length > 0 && (
                                                    <p data-testid="rams-prefill-review-note" className="text-[11.5px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-2 mb-3">
                                                        If any answers here were AI pre-filled from your CPP/documents, please review every one — especially the hazard checklist — before submitting.
                                                    </p>
                                                )}
                                                <RamsRuntimeForm
                                                    key={runtimeVersion}
                                                    sections={runtime.sections} initialAnswers={runtime.answers} initialFiles={runtime.files}
                                                    prefill={runtime.prefill} editable={runtime.editable} requestMeta={req}
                                                    aiPrefilledFields={runtime.ai_prefilled_fields}
                                                    saveApi={(body) => api.post(`/rams/requests/${requestId}/save`, body).then((r) => r.data)}
                                                    submitApi={(body) => api.post(`/rams/requests/${requestId}/submit`, body).then(async (r) => { await silentRefresh(); onChanged?.(); return r.data; })}
                                                    uploadApi={(fieldKey, file) => {
                                                        const fd = new FormData(); fd.append("field_key", fieldKey); fd.append("file", file);
                                                        return api.post(`/rams/requests/${requestId}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
                                                    }}
                                                />
                                            </div>
                                        )
                                )}
                            </div>
                        )}

                        {canEdit && ["Submitted", "Clarification Required", "AI Draft Generated", "Priority Risk Review"].includes(req.status) && (
                            <div className="pt-3 border-t border-[#F0EDE8] space-y-3">
                                {req.status === "Clarification Required" && (
                                    <div className="p-3 rounded-lg bg-[#FBEFE5] border border-[#F0D9C0]" data-testid="rams-clarification-banner">
                                        <p className="text-[11.5px] font-bold text-[#8A4A16]">Waiting on the client — clarification requested</p>
                                        {req.status_history?.length > 0 && (
                                            <p className="text-[11.5px] text-[#8A4A16] mt-0.5">
                                                {req.status_history.filter((h) => h.to === "Clarification Required").slice(-1)[0]?.note}
                                            </p>
                                        )}
                                    </div>
                                )}
                                {req.status === "Priority Risk Review" && (
                                    <div className="p-3 rounded-lg bg-[#EEF3FB] border border-[#CFE0F5]" data-testid="rams-review-banner">
                                        <p className="text-[11.5px] font-bold text-[#1E4E8C]">Under Priority Risk review — approve to publish the branded output to the Document Library, or send it back for changes</p>
                                    </div>
                                )}
                                {req.status === "Submitted" && (
                                    <RamsSupportingContextPicker requestId={requestId} selectedIds={supportingDocIds} onChange={setSupportingDocIds} />
                                )}
                                {["Submitted", "AI Draft Generated"].includes(req.status) && (
                                    <div className="flex flex-wrap items-center gap-2">
                                        <button data-testid="rams-detail-generate-draft" onClick={generateDraft} disabled={generateBusy}
                                            className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-1.5 rounded-lg text-white disabled:opacity-60"
                                            style={{ background: "var(--brand-accent, #C75A1B)" }}>
                                            {generateBusy ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                                            {req.status === "AI Draft Generated" ? "Regenerate AI draft" : "Generate AI draft"}
                                        </button>
                                        <button data-testid="rams-detail-clarify-toggle" onClick={() => setClarifyOpen((o) => !o)}
                                            className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-1.5 rounded-lg border border-[#E5E0DA] hover:bg-[#F6F4F0]">
                                            <MessageCircleQuestion size={12} /> Request clarification
                                        </button>
                                        {req.status === "AI Draft Generated" && (
                                            <button data-testid="rams-detail-send-for-review" onClick={sendForReview} disabled={reviewBusy}
                                                className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-1.5 rounded-lg border border-[#E5E0DA] hover:bg-[#F6F4F0] disabled:opacity-60">
                                                {reviewBusy ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Send for review
                                            </button>
                                        )}
                                        {generateBusy && <span className="text-[11px] text-[#9A968F]">Writing the RAMS draft — this can take up to a minute…</span>}
                                    </div>
                                )}
                                {req.status === "Priority Risk Review" && (
                                    <div className="flex flex-wrap items-center gap-2">
                                        <button data-testid="rams-detail-approve" onClick={approve} disabled={approveBusy}
                                            className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-1.5 rounded-lg text-white disabled:opacity-60"
                                            style={{ background: "var(--brand-accent, #C75A1B)" }}>
                                            {approveBusy ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />} Approve
                                        </button>
                                        <button data-testid="rams-detail-request-changes-toggle" onClick={() => setChangesOpen((o) => !o)}
                                            className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-1.5 rounded-lg border border-[#E5E0DA] hover:bg-[#F6F4F0]">
                                            <Undo2 size={12} /> Request changes
                                        </button>
                                    </div>
                                )}
                                {changesOpen && (
                                    <div className="p-3 rounded-lg bg-[#FBFAF8] border border-[#F0EDE8] space-y-2">
                                        <textarea data-testid="rams-changes-note" rows={2} value={changesNote} onChange={(e) => setChangesNote(e.target.value)}
                                            placeholder="What needs changing before this can be approved?"
                                            className="w-full px-3 py-2 rounded-lg border border-[#E5E0DA] bg-white text-[12.5px]" />
                                        <button data-testid="rams-changes-submit" onClick={requestChanges} disabled={changesBusy}
                                            className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-lg border border-[#E5E0DA] hover:bg-white disabled:opacity-60">
                                            {changesBusy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send back for changes
                                        </button>
                                    </div>
                                )}
                                {clarifyOpen && (
                                    <div className="p-3 rounded-lg bg-[#FBFAF8] border border-[#F0EDE8] space-y-2">
                                        <textarea data-testid="rams-clarify-note" rows={2} value={clarifyNote} onChange={(e) => setClarifyNote(e.target.value)}
                                            placeholder="What's missing or unclear? The client will be notified and the questionnaire reopened."
                                            className="w-full px-3 py-2 rounded-lg border border-[#E5E0DA] bg-white text-[12.5px]" />
                                        <button data-testid="rams-clarify-submit" onClick={requestClarification} disabled={clarifyBusy}
                                            className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-lg border border-[#E5E0DA] hover:bg-white disabled:opacity-60">
                                            {clarifyBusy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />} Send to client
                                        </button>
                                    </div>
                                )}
                                {["AI Draft Generated", "Priority Risk Review"].includes(req.status) && (
                                    <div className="flex items-center gap-2 pt-1">
                                        <button data-testid="rams-export-pdf" onClick={() => exportFile("pdf")} disabled={exportBusy !== ""}
                                            className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2.5 py-1.5 rounded-lg border border-[#E5E0DA] hover:bg-[#F6F4F0] disabled:opacity-60">
                                            {exportBusy === "pdf" ? <Loader2 size={11} className="animate-spin" /> : <FileDown size={11} />} PDF
                                        </button>
                                        <button data-testid="rams-export-docx" onClick={() => exportFile("docx")} disabled={exportBusy !== ""}
                                            className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2.5 py-1.5 rounded-lg border border-[#E5E0DA] hover:bg-[#F6F4F0] disabled:opacity-60">
                                            {exportBusy === "docx" ? <Loader2 size={11} className="animate-spin" /> : <FileText size={11} />} DOCX
                                        </button>
                                    </div>
                                )}
                                {["AI Draft Generated", "Priority Risk Review"].includes(req.status) && (
                                    <RamsDraftPanel requestId={requestId} onChanged={() => { load(); onChanged?.(); }} />
                                )}
                            </div>
                        )}

                        {((canEdit && ["Approved", "Published to Client", "Client Acknowledged"].includes(req.status)) ||
                            (!canEdit && ["Published to Client", "Client Acknowledged"].includes(req.status))) && (
                            <div className="pt-3 border-t border-[#F0EDE8] space-y-2" data-testid="rams-output-panel">
                                <p className="text-[12.5px] font-bold text-[#1F1F1F] flex items-center gap-1.5">
                                    <CheckCircle2 size={13} className="text-emerald-600" />
                                    {req.status === "Approved" ? `Approved — revision ${req.latest_revision} saved to the Document Library` :
                                        req.status === "Published to Client" ? "Published to the client's Document Library" :
                                            "Acknowledged by the client"}
                                </p>
                                {canEdit && (
                                    <div className="flex items-center gap-2">
                                        <button data-testid="rams-export-pdf" onClick={() => exportFile("pdf")} disabled={exportBusy !== ""}
                                            className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2.5 py-1.5 rounded-lg border border-[#E5E0DA] hover:bg-[#F6F4F0] disabled:opacity-60">
                                            {exportBusy === "pdf" ? <Loader2 size={11} className="animate-spin" /> : <FileDown size={11} />} PDF
                                        </button>
                                        <button data-testid="rams-export-docx" onClick={() => exportFile("docx")} disabled={exportBusy !== ""}
                                            className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2.5 py-1.5 rounded-lg border border-[#E5E0DA] hover:bg-[#F6F4F0] disabled:opacity-60">
                                            {exportBusy === "docx" ? <Loader2 size={11} className="animate-spin" /> : <FileText size={11} />} DOCX
                                        </button>
                                    </div>
                                )}
                                {["Published to Client", "Client Acknowledged"].includes(req.status) && (
                                    <button data-testid="rams-download-published" onClick={downloadPublished} disabled={exportBusy !== ""}
                                        className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2.5 py-1.5 rounded-lg border border-[#E5E0DA] hover:bg-[#F6F4F0] disabled:opacity-60">
                                        {exportBusy === "published" ? <Loader2 size={11} className="animate-spin" /> : <FileDown size={11} />} Download published document
                                    </button>
                                )}
                                {canEdit && req.status === "Approved" && (
                                    <button data-testid="rams-detail-publish" onClick={publish} disabled={publishBusy}
                                        className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-1.5 rounded-lg text-white disabled:opacity-60"
                                        style={{ background: "var(--brand-accent, #C75A1B)" }}>
                                        {publishBusy ? <Loader2 size={12} className="animate-spin" /> : <UploadCloud size={12} />} Publish to client
                                    </button>
                                )}
                                {req.status === "Published to Client" && (
                                    <div className="p-3 rounded-lg bg-[#EEF3FB] border border-[#CFE0F5] space-y-2" data-testid="rams-acknowledge-banner">
                                        <p className="text-[11.5px] font-bold text-[#1E4E8C]">
                                            {canEdit ? "Awaiting the client's acknowledgement — you can also record it on their behalf." : "Please confirm you've received and briefed the team on this document."}
                                        </p>
                                        {!ackOpen ? (
                                            <button data-testid="rams-detail-acknowledge-toggle" onClick={() => setAckOpen(true)}
                                                className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-white border border-[#CFE0F5] text-[#1E4E8C] hover:bg-[#F6F4F0]">
                                                <ThumbsUp size={12} /> Acknowledge receipt
                                            </button>
                                        ) : (
                                            <div className="space-y-2">
                                                <textarea data-testid="rams-acknowledge-note" rows={2} value={ackNote} onChange={(e) => setAckNote(e.target.value)}
                                                    placeholder="Optional note (e.g. confirming who briefed the team)"
                                                    className="w-full px-3 py-2 rounded-lg border border-[#CFE0F5] bg-white text-[12.5px]" />
                                                <button data-testid="rams-acknowledge-submit" onClick={acknowledge} disabled={ackBusy}
                                                    className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-[#1E4E8C] text-white disabled:opacity-60">
                                                    {ackBusy ? <Loader2 size={12} className="animate-spin" /> : <ThumbsUp size={12} />} Confirm acknowledgement
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                )}
                                {req.status === "Client Acknowledged" && (
                                    <div className="p-3 rounded-lg bg-[#EAF7EE] border border-[#CDE9D4]" data-testid="rams-acknowledged-info">
                                        <p className="text-[11.5px] font-bold text-[#1E6B3C]">
                                            Acknowledged by {req.acknowledged_by_name} — {req.acknowledged_at ? new Date(req.acknowledged_at).toLocaleString("en-GB") : ""}
                                        </p>
                                        {req.acknowledgement_note && <p className="text-[11.5px] text-[#1E6B3C] mt-0.5">{req.acknowledgement_note}</p>}
                                    </div>
                                )}
                                {canEdit && (
                                    <>
                                        <button data-testid="rams-detail-supersede-toggle" onClick={() => setSupersedeOpen((o) => !o)}
                                            className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold px-2.5 py-1.5 rounded-lg text-[#9A968F] hover:text-red-600">
                                            <History size={11} /> Supersede
                                        </button>
                                        {supersedeOpen && (
                                            <div className="p-3 rounded-lg bg-[#FBFAF8] border border-[#F0EDE8] space-y-2">
                                                <textarea data-testid="rams-supersede-note" rows={2} value={supersedeNote} onChange={(e) => setSupersedeNote(e.target.value)}
                                                    placeholder="Optional note — why is this being superseded?"
                                                    className="w-full px-3 py-2 rounded-lg border border-[#E5E0DA] bg-white text-[12.5px]" />
                                                <button data-testid="rams-supersede-submit" onClick={supersede} disabled={supersedeBusy}
                                                    className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 disabled:opacity-60">
                                                    {supersedeBusy ? <Loader2 size={12} className="animate-spin" /> : <History size={12} />} Confirm supersede
                                                </button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        )}

                        <div className="pt-3 border-t border-[#F0EDE8]">
                            <button data-testid="rams-detail-toggle-audit" onClick={toggleAudit}
                                className="w-full flex items-center justify-between text-[12.5px] font-bold text-[#1F1F1F]">
                                <span className="flex items-center gap-1.5"><Activity size={13} /> Activity</span>
                                {auditOpen ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                            </button>
                            {auditOpen && (
                                auditLoading ? <div className="py-4 text-center text-[#9A968F]"><Loader2 className="inline animate-spin" size={14} /></div> :
                                    <ol className="mt-2.5 space-y-2" data-testid="rams-audit-list">
                                        {(auditEntries || []).length === 0 ? (
                                            <li className="text-[12px] text-[#9A968F]">No activity recorded yet.</li>
                                        ) : auditEntries.map((e, i) => (
                                            <li key={i} className="text-[12px] pl-2.5 border-l-2 border-[#E5E0DA]">
                                                <p className="font-semibold text-[#1F1F1F]">{e.label}</p>
                                                <p className="text-[#9A968F]">{e.by_name || "—"} · {e.at ? new Date(e.at).toLocaleString("en-GB") : ""}</p>
                                                {e.note && <p className="text-[#6F6A64] mt-0.5">{e.note}</p>}
                                            </li>
                                        ))}
                                    </ol>
                            )}
                        </div>


                        <div className="pt-3 border-t border-[#F0EDE8] grid grid-cols-2 gap-2">
                            <div>
                                <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#6F6A64] mb-1">Assigned consultant</label>
                                <select data-testid="rams-detail-consultant" value={consultantId} disabled={!canEdit}
                                    onChange={(e) => setConsultantId(e.target.value)} className={inputCls}>
                                    <option value="">Unassigned</option>
                                    {staff.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#6F6A64] mb-1">Due date</label>
                                <input data-testid="rams-detail-due-date" type="date" value={dueDate || ""} disabled={!canEdit}
                                    onChange={(e) => setDueDate(e.target.value)} className={inputCls} />
                            </div>
                        </div>
                        {canEdit && (
                            <button data-testid="rams-detail-save-reassign" onClick={saveReassign} disabled={savingReassign}
                                className="inline-flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-lg border border-[#E5E0DA] hover:bg-[#F6F4F0] disabled:opacity-60">
                                {savingReassign ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />} Save
                            </button>
                        )}
                    </div>
                )}
                {canEdit && req && (
                    <div className="px-5 py-3 border-t border-[#E5E0DA] flex items-center justify-end gap-2 bg-[#F5F3F0]">
                        {req.status === "Draft Request" && (
                            <button data-testid="rams-detail-issue" onClick={issue}
                                className="inline-flex items-center gap-1.5 text-[13px] font-semibold px-3 py-1.5 rounded-lg text-white"
                                style={{ background: "var(--brand-accent, #C75A1B)" }}><Send size={12} />Issue</button>
                        )}
                        <button data-testid="rams-detail-duplicate" onClick={duplicate}
                            className="inline-flex items-center gap-1.5 text-[13px] font-semibold px-3 py-1.5 rounded-lg border border-[#E5E0DA] hover:bg-white"><Copy size={12} />Duplicate</button>
                        {!["Approved", "Published to Client", "Client Acknowledged"].includes(req.status) && (
                            <button data-testid="rams-detail-archive" onClick={archive}
                                className="inline-flex items-center gap-1.5 text-[13px] font-semibold px-3 py-1.5 rounded-lg text-[#9A968F] hover:text-red-600"><ArchiveIcon size={12} />Archive</button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

function InfoRow({ label, value }) {
    return <div><p className="text-[10px] font-semibold uppercase tracking-wider text-[#9A968F]">{label}</p><p className="text-[#1F1F1F]">{value}</p></div>;
}
